import React from 'react';

import { cn } from '../../utils/cn.ts';
import { Button, type ButtonProps } from './Button.tsx';
import { Icon } from './Icon.tsx';

export interface SplitButtonItem {
    destructive?: boolean;
    disabled?: boolean;
    icon?: string;
    id: string;
    label: string;
    onSelect: () => void;
}

export interface SplitButtonProps {
    className?: string;
    disabled?: boolean;
    icon?: string;
    items: readonly SplitButtonItem[];
    label: string;
    menuLabel: string;
    onClick: () => void;
    size?: ButtonProps['size'];
    variant?: ButtonProps['variant'];
}

export const SplitButton: React.FC<SplitButtonProps> = ({
    className,
    disabled = false,
    icon,
    items,
    label,
    menuLabel,
    onClick,
    size = 'md',
    variant = 'primary',
}) => {
    const [open, setOpen] = React.useState(false);
    const menuId = React.useId();
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
    const menuDisabled = disabled || items.length === 0;

    React.useEffect(() => {
        if (menuDisabled) setOpen(false);
    }, [menuDisabled]);

    React.useEffect(() => {
        if (!open) return;
        itemRefs.current[0]?.focus();
    }, [open]);

    React.useEffect(() => {
        if (!open || typeof document === 'undefined') return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (rootRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [open]);

    const closeMenu = (returnFocus = false) => {
        setOpen(false);
        if (returnFocus) triggerRef.current?.focus();
    };

    const focusMenuItem = (index: number) => {
        const itemCount = items.length;
        if (itemCount === 0) return;
        const normalizedIndex = ((index % itemCount) + itemCount) % itemCount;
        itemRefs.current[normalizedIndex]?.focus();
    };

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeMenu(true);
            return;
        }

        const activeElement = document.activeElement;
        const currentIndex = activeElement instanceof HTMLButtonElement
            ? itemRefs.current.indexOf(activeElement)
            : -1;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            focusMenuItem(currentIndex + 1);
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            focusMenuItem(currentIndex - 1);
            return;
        }
        if (event.key === 'Home') {
            event.preventDefault();
            focusMenuItem(0);
            return;
        }
        if (event.key === 'End') {
            event.preventDefault();
            focusMenuItem(items.length - 1);
        }
    };

    return (
        <div className={cn('relative inline-flex', className)} ref={rootRef}>
            <Button
                className="rounded-r-none"
                disabled={disabled}
                leftIcon={icon}
                onClick={onClick}
                size={size}
                variant={variant}
            >
                {label}
            </Button>
            <Button
                aria-controls={open ? menuId : undefined}
                aria-expanded={open}
                aria-haspopup="menu"
                aria-label={menuLabel}
                className="min-w-0 rounded-l-none border-black/10 border-l px-2 dark:border-white/10"
                disabled={menuDisabled}
                onClick={() => setOpen((current) => !current)}
                ref={triggerRef}
                size={size}
                title={menuLabel}
                variant={variant}
            >
                <Icon name="expand_more" size="sm" />
            </Button>
            {open ? (
                <div
                    className="absolute top-full right-0 z-50 mt-2 min-w-56 rounded-lg border border-gray-200 bg-white p-1.5 shadow-black/10 shadow-xl focus:outline-none dark:border-border-dark dark:bg-surface-dark dark:shadow-black/30"
                    id={menuId}
                    onKeyDown={handleMenuKeyDown}
                    role="menu"
                >
                    {items.map((item, index) => (
                        <Button
                            aria-disabled={item.disabled || undefined}
                            className={cn(
                                'w-full justify-start rounded-md px-2 py-1.5 text-left font-medium text-sm',
                                item.destructive && 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20',
                                item.disabled && 'opacity-50'
                            )}
                            key={item.id}
                            onClick={() => {
                                if (disabled || item.disabled) return;
                                item.onSelect();
                                closeMenu(true);
                            }}
                            ref={(node) => {
                                itemRefs.current[index] = node;
                            }}
                            role="menuitem"
                            size="none"
                            tabIndex={-1}
                            variant="plain"
                        >
                            {item.icon ? <Icon name={item.icon} size="sm" /> : null}
                            <span>{item.label}</span>
                        </Button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

SplitButton.displayName = 'SplitButton';
